import {
  Badge,
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { reactEvents } from '@bridge/reactEvents';
import {
  LinkedUserEntity,
  UserContactLink,
} from '@texas/api/endpoints/userApi';
import { ServerError } from '@texas/types';
import { useTranslation } from 'react-i18next';
import { ErrorDetails } from '../shared/alert/ErrorDetails';

interface UserContactLinksProps {
  contacts: UserContactLink[];
  loading: boolean;
  error: ServerError | null;
}

export function UserContactLinks({
  contacts,
  loading,
  error,
}: UserContactLinksProps) {
  const { t } = useTranslation();
  if (error) return <ErrorDetails error={error} />;
  if (loading) return <Spinner />;
  if (!contacts.length)
    return (
      <Text color="entityDirectory.mutedText">
        {t('userDetails.noLinkedContacts')}
      </Text>
    );

  return (
    <Flex direction="column" gap={4}>
      <Text color="entityDirectory.mutedText">
        {t('userDetails.contactLinksDescription')}
      </Text>
      {contacts.map((contact) => (
        <Box
          key={contact.id}
          bg="entityDirectory.surface"
          border="1px solid"
          borderColor="entityDirectory.border"
          p={5}
        >
          <Flex gap={3} align="center" wrap="wrap">
            <Text fontWeight="bold">{contact.name}</Text>
            {contact.archived && <Badge>{t('general.archived')}</Badge>}
          </Flex>
          <Text mb={4} color="entityDirectory.mutedText">
            {contact.email}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <EntityLinks entities={contact.customers} supplier={false} />
            <EntityLinks entities={contact.suppliers} supplier={true} />
          </SimpleGrid>
        </Box>
      ))}
    </Flex>
  );
}

function EntityLinks({
  entities,
  supplier,
}: {
  entities: LinkedUserEntity[];
  supplier: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Flex direction="column" align="start" gap={2}>
      <Text fontWeight="semibold">
        {t(supplier ? 'general.suppliers' : 'general.customers')}
      </Text>
      {!entities.length && (
        <Text color="entityDirectory.mutedText">{t('general.none')}</Text>
      )}
      {entities.map((entity) => (
        <Flex key={entity.id} gap={2} align="center">
          <Button
            variant="link"
            whiteSpace="normal"
            textAlign="left"
            onClick={() =>
              reactEvents.routingChanged.dispatch({
                to: supplier ? 'supplier.management' : 'customer.management',
                navObj: supplier
                  ? { supplierId: entity.id }
                  : { customerId: entity.id },
              })
            }
          >
            {entity.name}
          </Button>
          {entity.archived && <Badge>{t('general.archived')}</Badge>}
        </Flex>
      ))}
    </Flex>
  );
}
